import { isPosition, normalizeListingForm, positionSubmitErrors } from "./listingKind";
/**
 * @file caseService.js
 * @description Case data access layer. Handles case loading, posting, editing,
 *   status transitions, archiving, freshness management, and view tracking.
 *   Provides the dbToCase transform that maps DB rows to UI-shaped case objects.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module lib/caseService
 */
import { supabase } from "./supabase";
import { spendCapInfoFrom } from "./spendCap";
import { getCachedConfig } from "./configService";
import { PAGE_SIZE } from "./findCasesQuery";
import { spendCredits, getBoostProducts } from "./boostService";
import { loadAllConfigs } from "./configService";
import { trackEvent, EVENTS } from "./eventService";
import { geocodeZip } from "./geocode";
import { sumWeeklyHours } from "./authorizations";
import { normalizePeriods, sortDays } from "./schedulePeriods";
import { displayCaseCode } from "../theme";
import { logCaughtError } from "./errorService";
import { chosenMustHaves, keySkillsFor } from "./applicantFitEngine";

// Best-effort case coordinates. A case with null lat/lng is invisible to the
// distance-based therapist marketplace, so we try hard to resolve them — the
// client's coords first, then a server-independent ZIP lookup (retried once)
// when the client's geocode failed or raced. We do NOT block posting on a
// geocode failure — a transient API outage must not lose the agency's work.
// A null result is surfaced in "Needs Attention" (and editing re-geocodes), so
// an un-pinned case is flagged for a one-step manual fix, never silently lost.
async function resolveCaseCoords(form, coords) {
  let geo = coords;
  if ((!geo?.lat || !geo?.lng) && /^\d{5}$/.test(form?.zip || "")) {
    geo = await geocodeZip(form.zip);
    if (!geo?.lat || !geo?.lng) geo = await geocodeZip(form.zip); // one retry
  }
  return (geo?.lat && geo?.lng) ? geo : null;
}

// ── Freshness / expiry config helpers ─────────────────────────────────────────
// Reads from the platform_config cache (loaded at app startup via loadAllConfigs).
// Falls back to CONFIG_DEFAULTS if the cache is empty.
function normalizeCaseType(type) {
  const t = String(type || "RBT").trim().toLowerCase();
  return t === "bcba" || t === "bcaba" || t === "rbt" ? t : "rbt";
}

function getFreshnessWindowDays(type) {
  const normalized = normalizeCaseType(type);
  const configured = Number(getCachedConfig(`freshness_window_${normalized}`));
  if (Number.isFinite(configured) && configured > 0) return configured;
  return normalized === "bcba" ? 21 : 14;
}

function getStaleWindowDays(type) {
  const normalized = normalizeCaseType(type);
  const configured = Number(getCachedConfig(`stale_window_${normalized}`));
  if (Number.isFinite(configured) && configured > 0) return configured;
  return normalized === "bcba" ? 60 : 30;
}

function buildExpiryIso(type, now = new Date()) {
  const staleWindowDays = getStaleWindowDays(type);
  return new Date(now.getTime() + staleWindowDays * 86_400_000).toISOString();
}

// ── Freshness state — STRAT-R7-3: per-credential-type thresholds ──────────────
function freshnessState(confirmedAt, status, type) {
  if (status === "paused") return "paused";
  if (status === "filled") return "filled";
  if (!confirmedAt) return "stale";
  const freshnessWindow = getFreshnessWindowDays(type);
  const staleWindow = getStaleWindowDays(type);
  const daysSince = Math.floor((Date.now() - new Date(confirmedAt).getTime()) / 86_400_000);
  if (daysSince <= freshnessWindow) return "fresh";
  if (daysSince <= staleWindow)     return "due_soon";
  return "stale";
}

/** One pay parser for the app (lib/payRange): the posting wizard, the edit form and the Insights money math. */
export { parsePayRange } from "./payRange";

/**
 * @description Build the display `pay_range` string the DB stores, from the
 *   wizard's raw pay fields. The inverse of parsePayRange(), and the single
 *   place that formats pay for persistence so formToDb() and the preview
 *   builder can never drift apart.
 *
 *   Sanitizes input — strips "$", commas, and stray letters — so a value the
 *   agency actually types ("65,000", "48k", "$60k") can never again write
 *   "$NaNk/yr" into the column. Returns null when nothing parseable is given.
 *
 *   Units: the annual field collects $k/yr (label "($k/yr)", e.g. 48 → $48k),
 *   so we do NOT divide by 1000 (the old bug did, turning a clean "48" into
 *   "$0k"). We also tolerate agencies who type the full salary instead
 *   (48000 / "48,000") by folding anything ≥ 1000 down to $k. Hourly stays raw
 *   dollars. Output matches the canonical format: "$48–58k/yr" / "$18–24/hr".
 *
 * @param {string|number} payMin
 * @param {string|number} payMax
 * @param {string} payPeriod - "annual" | "hourly"
 * @returns {string|null} display string, or null when no pay is parseable
 */
export function formatPayRange(payMin, payMax, payPeriod) {
  const num = (v) => {
    const n = parseFloat(String(v ?? "").replace(/[^0-9.]/g, ""));
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const annual = payPeriod === "annual";
  // Annual: normalize to $k (fold full-dollar entries ≥ 1000 down). Hourly: raw.
  const norm = (n) => (n == null ? null : annual ? Math.round(n >= 1000 ? n / 1000 : n) : n);
  const lo = norm(num(payMin));
  const hi = norm(num(payMax));
  if (lo == null && hi == null) return null;
  const unit = annual ? "k/yr" : "/hr";
  if (lo != null && hi != null) return `$${lo}–${hi}${unit}`;
  return `$${lo ?? hi}${unit}`;
}

/**
 * @description Parse the `service_time` column into its structured schedule
 *   parts. The column stores EITHER a legacy free-text string OR a JSON blob
 *   ({ days, periods, timeRanges, sessionHours, assessmentTotalHours }). This
 *   is the single source of truth for reading it, so every surface (therapist,
 *   agency, admin) gets an identical shape and none of them ever leaks the raw
 *   JSON into the UI — the `serviceTime` field is always a short display
 *   summary (the day list), never the JSON blob.
 * @param {string} rawServiceTime - the raw cases.service_time column value
 * @returns {{serviceTime:string, serviceDays:string[], servicePeriods:Object,
 *   serviceTimeRanges:Object, sessionHours:Object, assessmentTotalHours:(number|null)}}
 */
export function parseServiceTime(rawServiceTime) {
  const raw = rawServiceTime || "";
  const empty = {
    serviceTime: "",
    serviceDays: [],
    servicePeriods: {},
    serviceTimeRanges: {},
    sessionHours: {},
    assessmentTotalHours: null,
  };
  if (!raw) return empty;
  let parsed = null;
  try { parsed = JSON.parse(raw); } catch { /* not JSON — legacy free-text */ }
  if (parsed && typeof parsed === "object") {
    const days = Array.isArray(parsed.days) ? parsed.days : [];
    return {
      // Short summary (day list) so the detail tile never dumps raw JSON; the
      // full breakdown is rendered from serviceDays/periods + SessionHoursTable.
      serviceTime: days.length ? sortDays(days).join(", ") : "",
      serviceDays: sortDays(days),
      servicePeriods: normalizePeriods(parsed.periods),
      serviceTimeRanges: parsed.timeRanges || {},
      sessionHours: parsed.sessionHours || {},
      // One-time Assessment total; falls back to a legacy sessionHours.Assessment
      // value for cases posted before the split.
      assessmentTotalHours: parsed.assessmentTotalHours ?? (parsed.sessionHours?.Assessment ?? null),
    };
  }
  // Legacy free-text service_time — surface it as-is, no structured parts.
  return { ...empty, serviceTime: raw };
}

/**
 * @description Maps a raw Supabase cases row to the UI-shaped case object.
 *   Parses JSON fields, computes freshness state, and derives display values.
 * @param {Object} row - Raw Supabase cases row
 * @returns {Object} UI-shaped case object
 */
export function dbToCase(row) {
  const createdAt = row.created_at ? new Date(row.created_at) : new Date();
  const daysAgo   = Math.floor((Date.now() - createdAt.getTime()) / 86_400_000);
  const freshness = freshnessState(row.confirmed_at, row.status, row.type);
  const svc       = parseServiceTime(row.service_time);
  return {
    id:                   row.id,
    caseCode:             displayCaseCode(row.case_code) || "",
    agencyId:             row.agency_id,
    // Therapist-facing name = the agency's Display Name (DBA), falling back to
    // the legal name so it is never blank. Set on every case-card/map/detail read.
    agency:               row.dba_name || row.agency_name || "Agency",
    agencyLegalName:      row.agency_name  || "Agency",
    type:                 row.type,
    listingKind:          row.listing_kind || "case",
    positionDetails:      row.listing_kind === "position" ? row.position_details || null : null,
    employment:           (row.employment || "").split(", ").filter(Boolean)[0] || "W2",
    employments:          row.employment ? row.employment.split(", ").filter(Boolean) : [],
    status:               row.status,
    // Weekly total. The hours column is numeric (fractional authorizations) and
    // PostgREST returns numeric as a string, so coerce to a number. Prefer the
    // exact sum recomputed from the per-authorization breakdown so the total
    // always matches the displayed lines.
    hours:                (() => {
                            const w = sumWeeklyHours(svc.sessionHours);
                            if (w > 0) return w;
                            return row.hours == null ? null : parseFloat(row.hours);
                          })(),
    payRange:             row.pay_range    || "",
    scheduleNotes:        row.schedule_notes || "",
    // Share page only: get_shared_case says whether notes exist without sending the text.
    hasScheduleNotes:     row.has_schedule_notes === true,
    startDate:            row.start_date   || "",
    // service_time parsing is centralized in parseServiceTime() so the summary
    // string + structured schedule parts stay consistent across every surface.
    serviceTime:          svc.serviceTime,
    serviceDays:          svc.serviceDays,
    servicePeriods:       svc.servicePeriods,
    sessionHours:         svc.sessionHours,
    assessmentTotalHours: svc.assessmentTotalHours,
    serviceTimeRanges:    svc.serviceTimeRanges,
    ageRange:             row.age_range    || "",
    age:                  row.age_range    || "",
    ageMin:               row.age_min      ?? null,
    ageMax:               row.age_max      ?? null,
    ageRangeDisplay:      (() => {
                            const n = parseInt(row.age_range);
                            if (!isNaN(n) && n > 0) return `${Math.max(2, n - 1)}–${n + 1}`;
                            return row.age_range || "";
                          })(),
    diagnosisTypes:       row.diagnosis_types || [],
    payerTypes:           row.payer_types     || [],
    // Case Fit must haves (board 4). null = fit engine defaults; see applicantFitEngine.mustHavesFor.
    mustHaves:            Array.isArray(row.must_haves) ? row.must_haves : null,
    mustHaveSkills:       row.must_have_skills || [],
    languages:            row.languages       || [],
    clientInitials:       row.client_initials || "",
    clientSex:            row.client_sex      || "Not Specified",
    therapistSexPref:     row.therapist_sex_pref || "Any",
    zip:                  row.zip          || "",
    city:                 row.city         || "",
    state:                row.state        || "",
    // Coords: 0 is the explicit "ungeocoded / invalid" sentinel. Consumers MUST
    // treat falsy lat/lng as "no coordinates" (e.g. MapView filters `!c.lat ||
    // !c.lng` before plotting / distance math) — no real ABA case sits at
    // (0,0). Keep this numeric contract; downstream distance code assumes a
    // number, so do NOT switch to null without auditing every consumer.
    lat:                  parseFloat(row.lat) || 0,
    lng:                  parseFloat(row.lng) || 0,
    setting:              (row.setting || "").split(", ").filter(Boolean)[0] || "",
    settings:             row.setting ? row.setting.split(", ").filter(Boolean) : [],
    deliveryMode:         row.delivery_mode || "in_person",
    skills:               row.skills       || [],
    sessionTypes:         row.session_types   || [],
    minExp:               row.min_exp      || "",
    supervision:          row.supervision  || "",
    trainingProvided:     row.training_provided    || false,
    bcbaSupport:          row.bcba_support         || false,
    mileageReimbursement: row.mileage_reimbursement || false,
    benefits:             row.benefits     || [],
    teamSize:             row.team_size,
    notes:                row.notes        || "",
    urgency:              row.urgency      || "",
    payMin:               row.pay_min      ?? null,
    // BCBA-specific
    caseloadSize:         row.caseload_size,
    billableHrs:          row.billable_hrs,
    rbtCount:             row.rbt_count,
    assessments:          row.assessments  || [],
    requirements:         row.requirements || [],
    supervisionModel:     row.supervision_model || null,
    // BCaBA-specific
    bcabaSupervisionProvided: row.bcaba_supervision || null,
    // Compliance attestation captured at posting time for BCaBA cases — see
    // formToDb. Default false (RBT/BCBA cases never collect this), so a UI
    // that wants to render "supervisor on staff: yes/no" only does so when
    // type === "BCABA" AND this is true.
    bcabaSupervisorOnStaff: !!row.bcaba_supervisor_on_staff,
    // Freshness
    confirmedAt:          row.confirmed_at || null,
    expiresAt:            row.expires_at   || null,
    freshness,
    // Archive
    archivedAt:           row.archived_at  || null,
    // Engagement
    views:                row.views ?? 0,
    // Coverage Gap diagnostic — computed by the daily cron + on-write trigger.
    // Drives the amber pin ring + card banner + filter chip on the Cases Map.
    // Status is one of: 'ok', 'gap_no_supply', 'gap_few_matches', 'gap_no_conversion'.
    coverageGapStatus:        row.coverage_gap_status        || "ok",
    matchedTherapistsCount:   row.matched_therapists_count   ?? 0,
    therapistsInRadiusCount:  row.therapists_in_radius_count ?? 0,
    coverageGapComputedAt:    row.coverage_gap_computed_at   || null,
    closedReason:             row.closed_reason || null,
    // Moderation flag — flagged cases are hidden from public discovery and
    // should be excluded from agency-side spend/efficiency rollups so they
    // don't skew "where my money is working" analytics.
    flagged:                  row.flagged ?? false,
    flagReason:               row.flagged_reason || null,
    flaggedAt:                row.flagged_at || null,
    // Ranking Engine signals (Phase 3) — precomputed on the cases row.
    // rankScore drives sort order in therapist discovery; activeBoostTypes
    // is the currently-active set of paid boosts, used by the recommendation
    // engine to avoid suggesting boosts the agency already owns.
    rankScore:           row.rank_score          ?? 0,
    activeBoostTypes:    row.active_boost_types  || [],
    // Tier + trend (Phase 6c) — denormalized from the daily snapshot cron.
    // rankTier: 'top' | 'strong' | 'above-median' | 'below-median' | 'stale' | null
    //   null = case not eligible for tier display (cohort too small or no coords)
    // rankTrend: 'rising' | 'falling' | 'steady' | null
    //   null = insufficient snapshot history (< 7 days)
    rankTier:            row.rank_tier  || null,
    rankTrend:           row.rank_trend || null,
    // Computed
    daysAgo,
    createdAt:            row.created_at   || null,
  };
}

// ── Rank tier estimation ───────────────────────────────────────────────────
/**
 * Derives an approximate rank tier from a precomputed rank_score when the
 * nightly cron hasn't yet written a confirmed tier to cases.rank_tier.
 * Thresholds are score-based, not cohort-relative — treat as an optimistic
 * estimate, not a guaranteed position. Used only in agency-facing UI.
 *
 * Score landmarks (typical):
 *   115  fresh case, no boosts  → above-median
 *   155+ spotlight or urgency   → strong
 *   200+ multiple boosts        → top
 *
 * @param {number} score
 * @returns {'top'|'strong'|'above-median'|'below-median'|null}
 */
export function estimateTierFromScore(score) {
  if (!score || score <= 0) return null;
  if (score >= 200) return "top";
  if (score >= 155) return "strong";
  if (score >= 100) return "above-median";
  return "below-median";
}

/**
 * @description Resolves a case's delivery_mode. Prefers an explicit
 *   form.deliveryMode (the posting-form radio); otherwise derives it from the
 *   settings list — Remote alongside in-person settings is hybrid, Remote-only
 *   is remote, anything else is in_person. Mirrors the DB backfill so old and
 *   new cases classify consistently.
 * @param {Object} form - Posting form (deliveryMode? and settings[])
 * @returns {"in_person"|"hybrid"|"remote"}
 */
export function deriveDeliveryMode(form) {
  const explicit = form?.deliveryMode;
  if (explicit === "in_person" || explicit === "hybrid" || explicit === "remote") {
    return explicit;
  }
  const settings = (form?.settings || []).map(s => String(s).toLowerCase());
  const hasRemote = settings.includes("remote");
  const hasInPerson = settings.some(s => ["home", "school", "clinic", "community"].includes(s));
  if (hasRemote && hasInPerson) return "hybrid";
  if (hasRemote) return "remote";
  return "in_person";
}

/**
 * @description Display chips for a case's "where/how", delivery-mode-aware so a
 *   remote case never shows a contradictory physical setting (e.g. "Clinic").
 *   Delivery mode leads; the physical setting only appears when in-person work
 *   is involved.
 *     remote    -> ["Remote"]
 *     hybrid    -> ["Remote", ...physicalSettings]  (remote + the in-person days)
 *     in_person -> [...physicalSettings]
 * @param {Object} c - Case (deliveryMode? + settings[]/setting)
 * @returns {string[]}
 */
export function caseSettingChips(c) {
  const base = (Array.isArray(c?.settings) && c.settings.length)
    ? c.settings
    : (c?.setting ? [c.setting] : []);
  const physical = base.filter(s => s && s !== "Remote");
  if (c?.deliveryMode === "remote") return ["Remote"];
  if (c?.deliveryMode === "hybrid") return ["Remote", ...physical];
  return physical.length ? physical : base.filter(Boolean);
}

// ── JS form → DB row ──────────────────────────────────────────────────────────
/**
 * The must-have columns for a posted case: the agency's choice (null keeps the engine defaults) and the pinned key
 * skills that are still selected. Positions carry none. Rules and limits come from the fit engine (which mirrors the
 * DB checks cases_must_haves_check and cases_must_have_skills_max).
 */
function mustHaveColumns(form) {
  if (isPosition(form)) return { must_haves: null, must_have_skills: [] };
  const listing = { mustHaves: form.mustHaves, mustHaveSkills: form.mustHaveSkills, skills: form.skills || [] };
  return { must_haves: chosenMustHaves(listing), must_have_skills: keySkillsFor(listing) };
}

function formToDb(userId, agencyName, form, coords) {
  form = normalizeListingForm(form);
  const now = new Date();
  return {
    agency_id:             userId,
    agency_name:           agencyName || null,
    type:                  form.role,
    listing_kind:          form.listingKind,
    ...(isPosition(form) ? { position_details: form.positionDetails } : {}),
    employment:            (form.employments || (form.employment ? [form.employment] : [])).join(", ") || null,
    status:                "active",
    hours:                 (() => {
                             // BCBA/BCaBA: the weekly authorizations (Behavior
                             // Treatment + Parent Training) ONLY — the one-time
                             // Assessment total is tracked separately and must not
                             // inflate the weekly hours.
                             // hours is numeric, so fractional weekly totals
                             // (half / quarter-hour authorizations) are stored
                             // exactly — no rounding.
                             if (form.sessionHours && Object.keys(form.sessionHours).length > 0) {
                               const total = sumWeeklyHours(form.sessionHours);
                               return total > 0 ? total : null;
                             }
                             return parseFloat(form.hours) || null;
                           })(),
    pay_range:             formatPayRange(form.payMin, form.payMax, form.payPeriod),
    pay_min:               (form.payMin && form.payPeriod !== "annual")
                             ? parseInt(form.payMin, 10) || null
                             : null,
    schedule_notes:        form.scheduleNotes   || null,
    start_date:            form.startDate       || null,
    service_time:          (() => {
                             const assessmentTotal = parseFloat(form.assessmentTotalHours) || null;
                             const hasDays    = form.serviceDays?.length > 0;
                             const hasSession = (form.sessionHours && Object.keys(form.sessionHours).length > 0) || assessmentTotal != null;
                             if (!hasDays && !hasSession) return form.serviceTime || null;
                             return JSON.stringify({
                               days:                 sortDays(form.serviceDays),
                               periods:              normalizePeriods(form.servicePeriods),
                               timeRanges:           form.serviceTimeRanges || {},
                               sessionHours:         form.sessionHours      || {},
                               assessmentTotalHours: assessmentTotal,
                             });
                           })(),
    age_range:             form.age || (form.ageMin && form.ageMax ? `${form.ageMin}–${form.ageMax}` : null),
    // When the agency enters a single age (form.age), derive age_min/age_max
    // using the same ±1 masking rule used for display so the DB filter works
    // correctly. Without this, single-age cases store NULL for both min/max and
    // the RPC's NULL-passthrough makes them match ANY age filter — a 4-year-old
    // case would appear in a "13–17" search. The ±1 range also intentionally
    // fuzzes the exact age for privacy (the same reason display shows "4–6").
    age_min:               (() => {
      if (form.ageMin) return parseInt(form.ageMin, 10) || null;
      const n = parseInt(form.age, 10);
      if (!isNaN(n) && n > 0) return Math.max(2, n - 1);
      return null;
    })(),
    age_max:               (() => {
      if (form.ageMax) return parseInt(form.ageMax, 10) || null;
      const n = parseInt(form.age, 10);
      if (!isNaN(n) && n > 0) return n + 1;
      return null;
    })(),
    diagnosis_types:       [
                             ...(form.diagnosisTypes || []),
                             ...(form.diagnosisOther?.trim() ? [form.diagnosisOther.trim()] : []),
                           ],
    languages:             form.languages       || [],
    client_initials:       form.clientInitials   || null,
    client_sex:            form.clientSex        || "Not Specified",
    therapist_sex_pref:    form.therapistSexPref || "Any",
    zip:                   form.zip             || null,
    city:                  coords?.city         || null,
    state:                 coords?.state        || null,
    lat:                   coords?.lat          || null,
    lng:                   coords?.lng          || null,
    setting:               (form.settings || (form.setting ? [form.setting] : [])).join(", ") || null,
    delivery_mode:         deriveDeliveryMode(form),
    skills:                form.skills          || [],
    payer_types:           form.payerTypes      || [],
    ...mustHaveColumns(form),
    session_types:         form.sessionTypes    || [],
    min_exp:               parseInt(form.minExp) || null,
    supervision:           form.supervision     || null,
    training_provided:     form.trainingProvided || false,
    bcba_support:          form.bcbaSupport      || false,
    mileage_reimbursement: form.mileageReimbursement || false,
    benefits:              form.benefits        || [],
    team_size:             parseInt(form.teamSize) || null,
    notes:                 form.notes           || null,
    // Urgency is no longer set directly from the form. It's a derived value
    // driven by active urgency_badge promotions in case_promotions (synced
    // via the urgency_badge_promo_sync trigger). The form's `urgency` field
    // is now an INTENT flag — handled by postCase/updateCase as a separate
    // spendCredits call after the row is committed.
    caseload_size:         parseInt(form.caseloadSize) || null,
    billable_hrs:          form.billableHrs     || null,
    rbt_count:             parseInt(form.rbtCount) || null,
    assessments:           form.assessments     || [],
    requirements:          form.requirements    || [],
    // supervision_model was written here as a phantom field — the form never
    // collected `supervisionModel`, so every write was a no-op null. Dropped
    // from the write path; the column stays in the schema for any historical
    // rows that have a value, and `dbToCase` keeps the read so SupervisionSection
    // surfaces it if a row ever has one. Re-introduce the write only when the
    // intent of the field is decided (in-vivo / remote / hybrid, etc.).
    bcaba_supervision:     form.bcabaSupervisionProvided || null,
    // BAA / compliance attestation — the agency confirms a supervising BCBA
    // is on staff before posting a BCaBA case (gated in CasePosting validation).
    // Before this fix the attestation was collected but never written; the form
    // blocked the post but the answer evaporated. Persist it so the audit trail
    // shows the agency confirmed at the time of posting. For RBT/BCBA roles the
    // value is forced false (the attestation is BCaBA-only) so it isn't
    // accidentally true on irrelevant case types.
    bcaba_supervisor_on_staff: form.role === "BCABA" ? !!form.bcabaSupervisorOnStaff : false,
    confirmed_at:          now.toISOString(),
    expires_at:            buildExpiryIso(form.role, now),
  };
}

/**
 * @description Builds an in-memory case object from in-flight wizard form
 *   state. Used by the wizard preview + publish-success surfaces so they
 *   render through the CaseViewer engine instead of duplicating layout
 *   logic. Goal: the agency's "this is how candidates will see your case"
 *   moment runs through the same parts that the published listing renders
 *   from. Mirrors the structure produced by `dbToCase` (camelCase, UI shape)
 *   so every CaseViewer part reads it the same way.
 *
 *   This is a pure transform — no I/O, no async. The synthetic object
 *   carries `__synthetic: true` so any future part that wants to detect
 *   "we're showing a preview, not a posted case" can branch on it.
 *
 * @param {Object} form - The CasePosting form state
 * @param {Object} [opts]
 * @param {Object} [opts.coords] - { city, state, lat, lng } from geocoder
 * @param {string} [opts.agencyId] - Agency user ID (for agency-self surfaces)
 * @param {string} [opts.agencyName] - Agency display name
 * @returns {Object} UI-shaped synthetic case (NOT a DB-backed entity)
 */
export function formToPreviewCase(form, { coords = null, agencyId = null, agencyName = null } = {}) {
  form = normalizeListingForm(form);
  const now = new Date();
  // Compute total hours the same way formToDb does: prefer per-session
  // breakdown when present, fall back to flat hours.
  const totalHours = (() => {
    if (form.sessionHours && Object.keys(form.sessionHours).length > 0) {
      const total = sumWeeklyHours(form.sessionHours); // weekly auths only — excludes one-time Assessment
      return total > 0 ? total : null;
    }
    return parseFloat(form.hours) || null;
  })();
  // Pay range string mirrors formToDb's formatting (shared helper).
  const payRange = formatPayRange(form.payMin, form.payMax, form.payPeriod) || "";
  const ageRange = form.age || (form.ageMin && form.ageMax ? `${form.ageMin}–${form.ageMax}` : "");

  return {
    __synthetic: true,                           // marker for "this is a preview, not a posted row"
    id:                       null,              // no DB id yet
    agencyId:                 agencyId,
    agency:                   agencyName || "Your agency",
    type:                     form.role || "RBT",
    listingKind:              form.listingKind,
    positionDetails:          isPosition(form) ? form.positionDetails : null,
    employment:               (form.employments || [])[0] || "W2",
    employments:              form.employments || [],
    status:                   "active",
    hours:                    totalHours,
    payRange,
    payMin:                   (form.payMin && form.payPeriod !== "annual")
                                ? parseInt(form.payMin, 10) || null
                                : null,
    scheduleNotes:            form.scheduleNotes || "",
    startDate:                form.startDate || "",
    serviceTime:              sortDays(form.serviceDays || []).join(", "),
    serviceDays:              form.serviceDays || [],
    servicePeriods:           form.servicePeriods || {},
    sessionHours:             form.sessionHours || {},
    assessmentTotalHours:     parseFloat(form.assessmentTotalHours) || null,
    serviceTimeRanges:        form.serviceTimeRanges || {},
    ageRange,
    age:                      ageRange,
    ageMin:                   (() => {
                                if (form.ageMin) return parseInt(form.ageMin, 10) || null;
                                const n = parseInt(form.age, 10);
                                if (!isNaN(n) && n > 0) return Math.max(2, n - 1);
                                return null;
                              })(),
    ageMax:                   (() => {
                                if (form.ageMax) return parseInt(form.ageMax, 10) || null;
                                const n = parseInt(form.age, 10);
                                if (!isNaN(n) && n > 0) return n + 1;
                                return null;
                              })(),
    ageRangeDisplay:          (() => {
                                const n = parseInt(form.age, 10);
                                if (!isNaN(n) && n > 0) return `${Math.max(2, n - 1)}–${n + 1}`;
                                return ageRange;
                              })(),
    diagnosisTypes:           [
                                ...(form.diagnosisTypes || []),
                                ...(form.diagnosisOther?.trim() ? [form.diagnosisOther.trim()] : []),
                              ],
    payerTypes:               form.payerTypes || [],
    mustHaves:                mustHaveColumns(form).must_haves,
    mustHaveSkills:           mustHaveColumns(form).must_have_skills,
    languages:                form.languages || [],
    clientInitials:           form.clientInitials || "",
    clientSex:                form.clientSex || "Not Specified",
    therapistSexPref:         form.therapistSexPref || "Any",
    zip:                      form.zip || "",
    city:                     coords?.city || "",
    state:                    coords?.state || "",
    lat:                      coords?.lat || 0,
    lng:                      coords?.lng || 0,
    setting:                  (form.settings || [])[0] || "",
    settings:                 form.settings || [],
    deliveryMode:             deriveDeliveryMode(form),
    skills:                   form.skills || [],
    sessionTypes:             form.sessionTypes || [],
    minExp:                   form.minExp || "",
    supervision:              form.supervision || "",
    trainingProvided:         !!form.trainingProvided,
    bcbaSupport:              !!form.bcbaSupport,
    mileageReimbursement:     !!form.mileageReimbursement,
    benefits:                 form.benefits || [],
    teamSize:                 parseInt(form.teamSize) || null,
    notes:                    form.notes || "",
    urgency:                  form.urgency === "high" ? "high" : "",  // preview only mirrors the intent
    caseloadSize:             parseInt(form.caseloadSize) || null,
    billableHrs:              form.billableHrs || null,
    rbtCount:                 parseInt(form.rbtCount) || null,
    assessments:              form.assessments || [],
    requirements:             form.requirements || [],
    supervisionModel:         null,                            // never collected in the form
    bcabaSupervisionProvided: form.bcabaSupervisionProvided || null,
    bcabaSupervisorOnStaff:   form.role === "BCABA" ? !!form.bcabaSupervisorOnStaff : false,
    confirmedAt:              now.toISOString(),
    expiresAt:                buildExpiryIso(form.role || "RBT", now),
    freshness:                "fresh",
    archivedAt:               null,
    views:                    0,
    coverageGapStatus:        "ok",
    matchedTherapistsCount:   0,
    therapistsInRadiusCount:  0,
    coverageGapComputedAt:    null,
    closedReason:             null,
    flagged:                  false,
    flagReason:               null,
    flaggedAt:                null,
    rankScore:                0,
    activeBoostTypes:         [],
    rankTier:                 null,
    rankTrend:                null,
    daysAgo:                  0,
    createdAt:                now.toISOString(),
  };
}

/**
 * @description Loads a single case by ID for the public share-link landing page.
 *   Works for guest and authenticated users. Returns null if not found or not active.
 *
 *   Reads through get_shared_case (SECURITY DEFINER), NOT the cases table. The
 *   function returns only what the page renders, so it never carries client
 *   initials, diagnosis types, internal counters, agency identity, lat/lng or the
 *   free-text columns (notes, schedule_notes). The share page shows ZIP and city,
 *   as before, and no longer shows the agency's free-text notes.
 *
 *   The same case is later opened in the provider finder after sign-in (App.js
 *   keeps it as `sharedCase`); the finder re-fetches the full provider-facing
 *   record with loadCasesByIds, so this lean shape is never what a provider sees
 *   in the detail modal.
 * @param {string} caseId - Case UUID
 * @returns {Promise<Object|null>} UI-shaped case object or null
 */
export async function loadCaseById(caseId) {
  if (!caseId) return null;
  try {
    const { data, error } = await supabase.rpc("get_shared_case", { p_case_id: caseId });
    if (error) return null;
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) return null;
    return dbToCase(row);
  } catch { return null; }
}

/**
 * @description Loads paginated cases for the discovery view using the search_cases_v3
 *   RPC with a privacy-safe retry. Returns rank-ordered results.
 * @param {string} role - The viewer's role (rbt, bcba, bcaba, guest)
 * @param {Object} [filters] - Filter options: offset, latBounds, filterType, filterEmp, etc.
 * @returns {Promise<{cases: Object[], total: number, hasMore: boolean, fallback?: boolean}>}
 */
export async function loadCases(role, filters = {}) {
  const {
    offset = 0,
    latBounds = null,
    filterType = "All",
    filterEmp = "All",
    filterSetting = "All",
    filterClientSex = "All",
    filterMinHours = "Any",
    filterSkills = [],
    filterPayers = [],
    filterAgeMin = "",
    filterAgeMax = "",
    filterMinPay = "",
    filterSupervision = "all",
    filterUrgency = "any",
    filterFreshness = "any",
    filterTrainingProvided = false,
    filterBcbaSupport = false,
    filterMileage = false,
  } = filters;

  const typeValues = Array.isArray(filterType) ? filterType : (filterType && filterType !== "All" ? [filterType] : []);
  const empValues = Array.isArray(filterEmp) ? filterEmp : (filterEmp && filterEmp !== "All" ? [filterEmp] : []);
  const settingValues = Array.isArray(filterSetting) ? filterSetting : (filterSetting && filterSetting !== "All" ? [filterSetting] : []);
  const clientSexValues = Array.isArray(filterClientSex) ? filterClientSex : (filterClientSex && filterClientSex !== "All" ? [filterClientSex] : []);
  const supervisionValues = Array.isArray(filterSupervision) ? filterSupervision : (filterSupervision && filterSupervision !== "all" ? [filterSupervision] : []);

  const requiresFallbackQuery = [typeValues, empValues, settingValues, clientSexValues, supervisionValues]
    .some(values => values.length > 1);

  const rpcParams = {
    p_role: role || null,
    p_type: typeValues.length === 1 ? typeValues[0] : null,
    p_min_lat: latBounds?.minLat ?? null,
    p_max_lat: latBounds?.maxLat ?? null,
    p_min_lng: latBounds?.minLng ?? null,
    p_max_lng: latBounds?.maxLng ?? null,
    p_filter_emp: empValues.length === 1 ? empValues[0] : null,
    p_filter_setting: settingValues.length === 1 ? settingValues[0] : null,
    p_filter_client_sex: clientSexValues.length === 1 ? clientSexValues[0] : null,
    p_filter_min_hours: !filterMinHours || filterMinHours === "Any" ? null : parseInt(filterMinHours, 10),
    p_filter_skills: filterSkills.length > 0 ? filterSkills : null,
    p_filter_payers: filterPayers.length > 0 ? filterPayers : null,
    p_filter_age_min: filterAgeMin ? parseInt(filterAgeMin, 10) || null : null,
    p_filter_age_max: filterAgeMax ? parseInt(filterAgeMax, 10) || null : null,
    p_filter_pay_min: filterMinPay ? parseInt(filterMinPay, 10) || null : null,
    p_filter_supervision: supervisionValues.length === 1 ? supervisionValues[0] : null,
    p_filter_urgency: (filterUrgency && filterUrgency !== "any") ? filterUrgency : null,
    p_filter_freshness_days: (filterFreshness && filterFreshness !== "any") ? parseInt(filterFreshness, 10) || null : null,
    p_filter_training: filterTrainingProvided || null,
    p_filter_bcba_support: filterBcbaSupport || null,
    p_filter_mileage: filterMileage || null,
    p_offset: offset,
    p_limit: PAGE_SIZE,
  };

  // Use the privacy-safe v3 projection for every feed: it includes listing_kind
  // and supports multiple roles. Retry a transient failure on the same endpoint;
  // never downgrade to a legacy projection or read non-owner table rows.
  const runFallbackQuery = async () => {
    const { data: v3, error: v3Err } = await supabase.rpc("search_cases_v3", {
      ...rpcParams,
      p_type: typeValues.length === 1 ? typeValues[0] : null,
      p_types: typeValues.length > 1 ? typeValues : null,
    });
    if (v3Err) throw v3Err;
    const total = v3?.[0]?.total_count ?? 0;
    const fallbackCases = (v3 || []).map(({ total_count, ...row }) => dbToCase(row));
    return { fallback: true, cases: fallbackCases, total, hasMore: offset + PAGE_SIZE < total };
  };

  if (requiresFallbackQuery) {
    return runFallbackQuery();
  }

  const { data, error } = await supabase.rpc("search_cases_v3", rpcParams);
  if (error) {
    return runFallbackQuery();
  }

  // RPC orders by rank_score DESC, confirmed_at DESC. No client-side re-sort.
  const total = data?.[0]?.total_count ?? 0;
  const cases = (data || []).map(({ total_count, ...row }) => dbToCase(row));
  return { cases, total, hasMore: offset + PAGE_SIZE < total };
}

/**
 * @description Loads remote/hybrid BCBA cases NATIONWIDE for a remote-electing
 *   BCBA — the "Remote opportunities" feed. Reuses search_cases_v3 with a NULL
 *   bounding box (no geo gate) + delivery-mode filter. Annotates each case with
 *   `licensedInState` (case state, or every required practice state for a position) and
 *   soft-sorts licensed-in-state first (then the RPC's rank order). This is a
 *   soft sort, NOT a filter — cases in states the BCBA isn't licensed in stay
 *   visible (agencies sponsor licensure). Returns [] for non-BCBA callers.
 * @param {Object} profile - Therapist profile (role, licensedStates[])
 * @param {number} [limit=100]
 * @returns {Promise<Object[]>} Remote case objects, licensed-in-state first
 */
export async function loadRemoteCasesForBcba(profile, limit = 100) {
  // Remote/nationwide discovery is for credentialed clinicians who can elect
  // remote work — BCBA and BCaBA (RBTs are always in-person). Each only sees
  // cases for their own credential.
  const role = (profile?.role || "").toLowerCase();
  if (role !== "bcba" && role !== "bcaba") return [];
  const { data, error } = await supabase.rpc("search_cases_v3", {
    p_type: role === "bcaba" ? "BCABA" : "BCBA",
    p_filter_delivery_mode: "remote",
    p_limit: limit,
  });
  if (error) return [];
  const licensed = new Set((profile.licensedStates || []).map(s => String(s).toUpperCase()));
  const cases = (data || []).map(({ total_count, ...row }) => {
    const c = dbToCase(row);
    // Every row here matched
    // p_filter_delivery_mode='remote' (remote OR hybrid), so mark it remote so
    // the card's remote/licensure badge renders.
    if (c.deliveryMode === "in_person") c.deliveryMode = "remote";
    c.licensedInState = isPosition(c)
      ? !!c.positionDetails?.requiredStates?.length && !c.positionDetails.statesToBeDetermined && c.positionDetails.requiredStates.every(s => licensed.has(String(s).toUpperCase()))
      : !!c.state && licensed.has(String(c.state).toUpperCase());
    return c;
  });
  // Stable soft-sort: licensed-in-state first; preserve RPC rank order within.
  return cases
    .map((c, i) => [c, i])
    .sort((a, b) => (b[0].licensedInState - a[0].licensedInState) || (a[1] - b[1]))
    .map(([c]) => c);
}

// ── National map pins ─────────────────────────────────────────────────────────
// The provider map plots EVERY open, discoverable case nationwide while the paged
// list stays radius based. Pins come from search_case_pins (a lightweight sibling
// of search_cases_v3: 13 narrow columns, same eligibility, no bounding box).
//
// Pin rows are cached in memory per filter set, so panning and zooming never
// refetch. The key is the exact RPC parameter set, so it changes only when a
// filter changes (the location and the radius are not part of it).

/** Server-side hard cap on pins (mirrors the clamp inside search_case_pins). */
export const PIN_ROW_CAP = 5000;
const PIN_CACHE_TTL_MS = 5 * 60 * 1000;
const PIN_CACHE_MAX_ENTRIES = 12;
const pinCache = new Map(); // key -> { at, value }

/** Clears the in-memory pin cache (a manual refresh, and tests). */
export function clearCasePinsCache() { pinCache.clear(); }

/**
 * @description Builds the search_case_pins RPC params from the discovery filter
 *   state. Mirrors loadCases' RPC params so the map and the list agree on
 *   filters, with three deliberate differences: NO bounding box (the map is
 *   national), age is sent (the list filters age client side, pins cannot), and
 *   multi select filters travel as arrays (the list falls back to client side
 *   filtering for those; a pin has too few fields to do that).
 *   A single selected value is sent as the scalar param, exactly as loadCases does.
 * @param {string} role - viewer role (rbt, bcba, bcaba, guest)
 * @param {Object} [filters] - same filter fields as loadCases
 * @returns {Object} RPC params
 */
export function buildPinsRpcParams(role, filters = {}) {
  const {
    filterType = "All", filterEmp = "All", filterSetting = "All", filterClientSex = "All",
    filterMinHours = "Any", filterSkills = [], filterPayers = [], filterAgeMin = "", filterAgeMax = "",
    filterMinPay = "", filterSupervision = "all", filterUrgency = "any", filterFreshness = "any",
    filterTrainingProvided = false, filterBcbaSupport = false, filterMileage = false,
  } = filters;
  const vals = (v, none) => Array.isArray(v) ? v : (v && v !== none ? [v] : []);
  const typeValues = vals(filterType, "All");
  const empValues = vals(filterEmp, "All");
  const settingValues = vals(filterSetting, "All");
  const sexValues = vals(filterClientSex, "All");
  const supervisionValues = vals(filterSupervision, "all");
  const one = (a) => (a.length === 1 ? a[0] : null);
  const many = (a) => (a.length > 1 ? a : null);
  return {
    p_role: role || null,
    p_type: one(typeValues),
    p_types: many(typeValues),
    p_filter_emp: one(empValues),
    p_emps: many(empValues),
    p_filter_setting: one(settingValues),
    p_settings: many(settingValues),
    p_filter_client_sex: one(sexValues),
    p_client_sexes: many(sexValues),
    p_filter_min_hours: !filterMinHours || filterMinHours === "Any" ? null : parseInt(filterMinHours, 10),
    p_filter_skills: filterSkills.length > 0 ? filterSkills : null,
    p_filter_payers: filterPayers.length > 0 ? filterPayers : null,
    p_filter_age_min: filterAgeMin ? parseInt(filterAgeMin, 10) || null : null,
    p_filter_age_max: filterAgeMax ? parseInt(filterAgeMax, 10) || null : null,
    p_filter_pay_min: filterMinPay ? parseInt(filterMinPay, 10) || null : null,
    p_filter_supervision: one(supervisionValues),
    p_supervisions: many(supervisionValues),
    p_filter_urgency: (filterUrgency && filterUrgency !== "any") ? filterUrgency : null,
    p_filter_freshness_days: (filterFreshness && filterFreshness !== "any") ? parseInt(filterFreshness, 10) || null : null,
    p_filter_training: filterTrainingProvided || null,
    p_filter_bcba_support: filterBcbaSupport || null,
    p_filter_mileage: filterMileage || null,
    p_limit: PIN_ROW_CAP,
  };
}

/**
 * @description Maps one search_case_pins row to the pin shape the map uses.
 *   Returns null for rows that cannot be plotted (no usable coordinates; 0 is the
 *   app-wide "ungeocoded" sentinel, see dbToCase). `isPin: true` marks it as the
 *   light record (no agency, hours, ages, skills), as opposed to a full case.
 */
export function mapPinRow(row) {
  const lat = parseFloat(row?.lat), lng = parseFloat(row?.lng);
  if (!row?.id || !lat || !lng) return null;
  const settings = row.setting ? String(row.setting).split(", ").filter(Boolean) : [];
  return {
    id: row.id,
    isPin: true,
    type: row.type || "",
    listingKind: row.listing_kind || "case",
    employment: row.employment || "",
    employments: row.employment ? String(row.employment).split(", ").filter(Boolean) : [],
    setting: settings[0] || "",
    settings,
    deliveryMode: row.delivery_mode || "in_person",
    payRange: row.pay_range || "",
    urgency: row.urgency || "",
    city: row.city || "",
    state: row.state || "",
    zip: row.zip || "",
    lat, lng,
    caseCode: row.case_code || "",
  };
}

/**
 * @description Loads the national pin set for the provider map. Never throws:
 *   if the RPC is unavailable (for example the migration is not applied yet)
 *   `failed` is true and the caller falls back to plotting the list's cases, which
 *   is exactly how the map behaved before this feature.
 * @param {string} role - viewer role
 * @param {Object} [filters] - discovery filter state (see buildPinsRpcParams)
 * @param {{force?:boolean}} [opts] - force bypasses the in-memory cache
 * @returns {Promise<{pins:Object[], capped:boolean, failed:boolean}>}
 */
export async function loadCasePins(role, filters = {}, { force = false } = {}) {
  const params = buildPinsRpcParams(role, filters);
  const key = JSON.stringify(params);
  const hit = pinCache.get(key);
  if (!force && hit && Date.now() - hit.at < PIN_CACHE_TTL_MS) return hit.value;
  try {
    const { data, error } = await supabase.rpc("search_case_pins", params);
    if (error) return { pins: [], capped: false, failed: true };
    const rows = Array.isArray(data) ? data : [];
    const value = {
      pins: rows.map(mapPinRow).filter(Boolean),
      capped: rows.length >= PIN_ROW_CAP,
      failed: false,
    };
    pinCache.delete(key);
    pinCache.set(key, { at: Date.now(), value });
    while (pinCache.size > PIN_CACHE_MAX_ENTRIES) pinCache.delete(pinCache.keys().next().value);
    return value;
  } catch {
    return { pins: [], capped: false, failed: true };
  }
}

// ── Load specific cases by ID (e.g. for saved cases outside the search area) ─
// Provider-facing record by id (saved cases, invited cases, a pin click, the share
// deep link opened in the finder). get_cases_by_ids returns the same columns the
// detail modal renders, never client initials, diagnosis types or internals.
export async function loadCasesByIds(ids) {
  if (!ids || ids.length === 0) return [];
  const { data, error } = await supabase.rpc("get_cases_by_ids", { p_ids: ids });
  if (error) return [];
  return (data || []).map(r => dbToCase(r));
}

// ── Search by case ID ───────────────────────────────────────────────────────
// Resolves a normalised case ID (see lib/caseCode.parseCaseCodeQuery) to a case.
// DELIBERATELY LOCATION AND FILTER BLIND: it sends only the code. A provider in
// Miami who types an Instagram card's RBT-1495 (Nebraska) lands on that case;
// the map's bounding box, radius, role filter and every discovery filter are not
// inputs here, so none of them can hide it.
//
// Two steps so the case is rendered by the same record every other entry point
// uses: lookup_case_by_code says whether the ID is open / no longer open /
// unknown (and returns only the uuid), then get_cases_by_ids (via
// loadCasesByIds) returns the provider-facing record.
//
// Resolves to one of:
//   { status: "open", case }   discoverable and accepting applicants
//   { status: "closed" }       exists but no longer open (no details, no id)
//   { status: "not_found" }    unknown, hidden or not visible to this viewer
//   { status: "error" }        the lookup itself failed (network, RPC missing)
export async function lookupCaseByCode(code) {
  if (!code) return { status: "not_found" };
  try {
    const { data, error } = await supabase.rpc("lookup_case_by_code", { p_code: code });
    if (error) return { status: "error" };
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) return { status: "not_found" };
    if (row.availability === "closed") return { status: "closed" };
    if (row.availability !== "open" || !row.case_id) return { status: "not_found" };
    const [full] = await loadCasesByIds([row.case_id]);
    return full ? { status: "open", case: full } : { status: "not_found" };
  } catch {
    return { status: "error" };
  }
}

// Short link /c/<CODE>: code -> case id of an OPEN case, or null (unknown,
// closed, hidden, or the lookup failed). The caller then loads the case exactly
// as a /share/<uuid> link does (loadCaseById), so what the visitor sees, and
// what is counted as a view, is identical. Returns only the id; no case detail.
export async function resolveOpenCaseIdByCode(code) {
  if (!code) return null;
  try {
    const { data, error } = await supabase.rpc("lookup_case_by_code", { p_code: code });
    if (error) return null;
    const row = Array.isArray(data) ? data[0] : data;
    return row && row.availability === "open" && row.case_id ? row.case_id : null;
  } catch { return null; }
}

/**
 * The server's entitlements for an agency (agency_entitlements): tier, has_paid_plan, read_only,
 * billing_starter_enabled, extra_slot_variant and grace_ends_at (when live openings pause; null unless tier none).
 * Display only. Returns null on any failure so the Billing page degrades to the slot check alone.
 * @param {string} agencyId
 */
export async function loadEntitlements(agencyId) {
  if (!agencyId) return null;
  const { data, error } = await supabase.rpc("agency_entitlements", { p_agency_id: agencyId });
  return error ? null : data;
}

// ── Slot availability — server-side gate for the seat model ──────────────────
// Returns { can_post, slots_used, slots_available, plan_limit, extra_slots,
// total_limit, plan }. plan_limit = -1 means unlimited (enterprise).
export async function checkPostingSlot(agencyId) {
  const { data, error } = await supabase.rpc("check_posting_slot", { p_agency_id: agencyId });
  if (error) {
    logCaughtError(error, "case_slot_check_failed", { step: "check_posting_slot" });
    throw error;
  }
  return data; // JSONB → plain object
}

// Present-tense count of therapists currently matching the agency's ACTIVE
// cases (role + within the therapist's own max_distance of a case). Drives the
// free "supply near your cases" Pulse signal. Returns 0 on any error so callers
// can zero-suppress (a standing "0" reads as a dead marketplace).
export async function countMatchingTherapists(agencyId) {
  if (!agencyId) return 0;
  const { data, error } = await supabase.rpc("count_matching_therapists", { p_agency_id: agencyId });
  if (error) { console.error("[count_matching_therapists]", error); return 0; }
  return Number(data) || 0;
}

/**
 * @description Adjust the agency's extra-slot subscription to a target
 *   quantity. The RPC computes the diff and acts atomically:
 *     - target > current → charge price × diff, grant new slots immediately,
 *       create or extend the subscription row, return invoice metadata.
 *     - target < current → schedule cancellation at current_period_end. The
 *       slot stays usable until the period rolls; quantity is the new
 *       commitment for next month's renewal.
 *     - target == current → noop.
 *
 *   Pricing is plan-differentiated: the server resolves the agency's per-slot
 *   $/mo via agency_slot_price_dollars() — extra_case_slot_price_pro for Pro,
 *   extra_case_slot_price_starter otherwise (legacy extra_case_slot_price is a
 *   fallback only).
 *
 * @param {string} agencyId
 * @param {number} targetQuantity - Final desired subscription size (0..50)
 * @returns {Promise<{ok: boolean, action?: string, new_quantity?: number, invoice_id?: string, invoice_number?: string, amount_cents?: number, next_period_end?: string, cancellations_pending?: number, effective_at?: string, error?: string}>}
 */
export async function manageExtraSlots(agencyId, targetQuantity, opts = {}) {
  if (!agencyId) return { ok: false, error: "agency_id required" };

  // Route through the Stripe-aware edge function. It decides internally
  // whether to call Stripe (live mode + existing extras subscription) or
  // fall back to the local manage_extra_slots RPC (test mode or no sub
  // yet). This keeps Stripe and our DB in sync — the previous direct-RPC
  // path was DB-only and would silently leave Stripe charging the OLD
  // quantity after a cancellation.
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    // Extra slots are only ever changed through the Stripe-aware edge function. The direct
    // manage_extra_slots RPC is service-role only since the 2026-10-17 billing audit.
    return { ok: false, error: "unauthenticated" };
  }

  try {
    const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe-manage-extra-slots`;
    const res = await fetch(url, {
      method:  "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization:  `Bearer ${session.access_token}`,
      },
      // end_starter: true is the explicit "End Starter plan" step. With the new plan model live the server refuses a
      // non-Pro agency going to quantity 0 without it (end_starter_confirm_required).
      body: JSON.stringify({ target_quantity: targetQuantity, ...(opts.endStarter === true ? { end_starter: true } : {}) }),
    });
    const json = await res.json().catch(() => ({}));
    if (!json?.ok) {
      // eslint-disable-next-line no-console
      console.warn("[caseService] manageExtraSlots edge fn failed:", json);
      // "use_checkout" (first purchase) and a spend cap block are expected, explained outcomes.
      if (json?.error !== "use_checkout" && !spendCapInfoFrom(json)) {
        logCaughtError(new Error(String(json?.error || "extra slots failed")), "extra_slots_failed", { op: "edge", httpStatus: res.status, slots: targetQuantity });
      }
      // Pass variant_key through so the caller can route a first-time purchase
      // (error === "use_checkout") into Stripe Checkout with the right price.
      return {
        ok: false,
        error: json?.error || "Could not adjust extra slots",
        hint: json?.hint,
        variant_key: json?.variant_key,
        // Spend cap block (Billing, Cost Control): details for the inline notice.
        spendCap: spendCapInfoFrom(json),
      };
    }
    return json;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[caseService] manageExtraSlots network error:", e);
    logCaughtError(e, "extra_slots_failed", { op: "network", slots: targetQuantity });
    return { ok: false, error: "network_error" };
  }
}

/**
 * @description Loads the agency's current extra-slot subscription state, or
 *   null if none. Used by the Billing UI to surface next billing date and
 *   any pending cancellations.
 * @param {string} agencyId
 * @returns {Promise<null|{quantity: number, current_period_start: string, current_period_end: string, grace_period_ends_at: string|null}>}
 */
export async function loadExtraSlotSubscription(agencyId) {
  if (!agencyId) return null;
  const { data, error } = await supabase
    .from("extra_slot_subscriptions")
    .select("quantity, current_period_start, current_period_end, grace_period_ends_at")
    .eq("agency_id", agencyId)
    .maybeSingle();
  if (error) {
    // eslint-disable-next-line no-console
    console.warn("[caseService] loadExtraSlotSubscription failed:", error);
    return null;
  }
  return data || null;
}

// ── PHI input scanner (BAA §2.3) ─────────────────────────────────────────────
// Scans free-text fields for patterns that indicate Protected Health Information
// was accidentally typed into a listing. Hard-blocks on SSNs; soft-warns on
// phone numbers and email addresses so the agency sees an actionable message.
const PHI_PATTERNS = [
  // Hard-block patterns — almost certainly sensitive identifiers
  { re: /\b\d{3}-\d{2}-\d{4}\b/,                          level: "block", label: "SSN (dashed)" },
  { re: /\b\d{3} \d{2} \d{4}\b/,                          level: "block", label: "SSN (spaced)" },
  // Soft-warn patterns — likely sensitive but might be legitimate
  { re: /\b[2-9]\d{2}[-.\s]?\d{3}[-.\s]?\d{4}\b/,        level: "warn",  label: "phone number" },
  { re: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/, level: "warn", label: "email address" },
];

function scanForPHI(fields) {
  const blocked = [];
  const warnings = [];
  for (const [fieldName, value] of Object.entries(fields)) {
    if (!value || typeof value !== "string") continue;
    for (const { re, level, label } of PHI_PATTERNS) {
      if (re.test(value)) {
        (level === "block" ? blocked : warnings).push(`${label} in "${fieldName}"`);
      }
    }
  }
  return { blocked, warnings };
}

/**
 * @description Posts a new case after enforcing NPI verification and slot availability.
 *   Throws with a typed error code (NPI_REQUIRED, TRIAL_EXPIRED, SLOT_LIMIT,
 *   PHI_DETECTED) on failure. Returns a phiWarnings array on the case object when
 *   soft-warn patterns are found so the UI can surface an advisory to the agency.
 * @param {string} userId - Agency user ID
 * @param {string} agencyName - Agency display name for the listing
 * @param {Object} form - Case form data from CasePosting
 * @param {Object|null} coords - Geocoded coords {lat, lng, city, state}
 * @returns {Promise<Object>} The created case as a UI-shaped object
 */
export async function postCase(userId, agencyName, form, coords) {
  const rawForm = form;
  form = normalizeListingForm(form);
  const positionErrors = positionSubmitErrors(rawForm, form);
  if (positionErrors.length) throw new Error(positionErrors.join(" "));
  // PHI gate — BAA §2.3 requires input controls to prevent submission of identifiers
  if (isPosition(form)) {
    const metadataContact = scanForPHI({ title: form.positionDetails.jobTitle, support: form.positionDetails.supportNotes, qualifications: form.positionDetails.qualifications });
    if (metadataContact.warnings.length) {
      const err = new Error('Remove phone numbers and email addresses from position details. Use platform messaging to share contact information.');
      err.code = 'CONTACT_INFO_DETECTED';
      throw err;
    }
  }
  const phi = scanForPHI({ notes: form.notes, schedule_notes: form.scheduleNotes, supervision: form.supervision, ...(isPosition(form) ? { position_title: form.positionDetails.jobTitle, support_notes: form.positionDetails.supportNotes, qualifications: form.positionDetails.qualifications } : {}) });
  if (phi.blocked.length > 0) {
    const err = new Error(
      `This listing appears to contain protected health information (${phi.blocked.join(", ")}). ` +
      `Please remove it before posting. ABAcases is not permitted to receive identifying client data.`
    );
    err.code = "PHI_DETECTED";
    err.phiPatterns = phi.blocked;
    throw err;
  }

  // NPI gate — enforce only when require_npi_to_post is enabled (default: true)
  const npiRequired = getCachedConfig("require_npi_to_post");
  if (npiRequired !== false) {
    const { data: agencyRow } = await supabase
      .from("agency_profiles")
      .select("npi_verified")
      .eq("user_id", userId)
      .maybeSingle();
    if (!agencyRow?.npi_verified) {
      const err = new Error("NPI verification is required before posting cases. Go to your Agency Profile to verify your NPI.");
      err.code = "NPI_REQUIRED";
      throw err;
    }
  }

  // BAA gate — clean client-side message; the DB trigger is the real enforcement.
  const baaRequired = getCachedConfig("require_baa_to_post");
  if (baaRequired !== false) {
    const { count: baaCount } = await supabase
      .from("baa_acceptances")
      .select("agency_id", { count: "exact", head: true })
      .eq("agency_id", userId);
    if (!baaCount) {
      const err = new Error("A signed HIPAA BAA is required before posting cases. Review and accept it in your Agency Profile.");
      err.code = "BAA_REQUIRED";
      throw err;
    }
  }

  // STRAT-R7-4: enforce slot limit + trial expiry server-side before inserting
  const slot = await checkPostingSlot(userId);
  if (slot.trial_expired) {
    const err = new Error("Your free trial has ended. Upgrade to Pro to post new cases.");
    err.code = "TRIAL_EXPIRED";
    err.slotInfo = slot;
    throw err;
  }
  if (!slot.can_post) {
    const err = new Error(
      `You've used all ${slot.slots_used} posting slot${slot.slots_used === 1 ? "" : "s"} on your ${slot.plan} plan. ` +
      `Close an existing listing to free a slot, or upgrade your plan for more.`
    );
    err.code = "SLOT_LIMIT";
    err.slotInfo = slot;
    throw err;
  }

  // Resolve the agency's LEGAL (agency_name) and DISPLAY (dba_name) names from
  // the profile so the denormalized case copies are authoritative and never null.
  // agency_name is the legal snapshot; dba_name is what therapists see (kept in
  // sync on later renames by the trg_sync_cases_dba_name trigger).
  let resolvedAgencyName = (agencyName || "").trim();
  let resolvedDbaName = "";
  {
    const { data: ap } = await supabase
      .from("agency_profiles").select("agency_name, dba_name").eq("user_id", userId).maybeSingle();
    resolvedAgencyName = resolvedAgencyName || ap?.agency_name || null;
    resolvedDbaName = ap?.dba_name || resolvedAgencyName || null;
  }

  const safeCoords = await resolveCaseCoords(form, coords);
  const row = formToDb(userId, resolvedAgencyName, form, safeCoords);
  // Denormalize the display name onto the case (parallel to agency_name). Set
  // here (not in formToDb) so updateCase never accidentally nulls it on an edit.
  row.dba_name = resolvedDbaName;
  const { data, error } = await supabase.from("cases").insert(row).select().single();
  if (error) throwCaseWriteError(error, "post", { step: "insert" });

  // Urgency-badge intent: if the agency toggled "Mark Urgent" on the form,
  // spend the credit now that we have the case_id. The trigger
  // urgency_badge_promo_sync will set cases.urgency = 'high' as a side effect.
  // Failure here is reported back to the caller — UI shows an inline warning
  // but the case stays posted (urgency is an add-on, not a blocker).
  if (form.urgency === "high") {
    try {
      const cfg = await loadAllConfigs().catch(() => null);
      const products = getBoostProducts(cfg);
      const result = await spendCredits(userId, {
        caseId: data.id,
        productKey: "urgency_badge",
        boostProducts: products,
      });
      if (!result?.ok) {
        // Decorate the returned case with a hint the UI can show.
        const c = dbToCase(data);
        c.urgencyBadgeError = result?.error || "Could not apply urgency badge.";
        return c;
      }
    } catch (e) {
      logCaughtError(e, "case_urgency_spend_failed", { listingId: data.id, step: "post_spend" });
      const c = dbToCase(data);
      c.urgencyBadgeError = e?.message || "Could not apply urgency badge.";
      return c;
    }
  }
  const result = dbToCase(data);
  if (phi.warnings.length > 0) result.phiWarnings = phi.warnings;
  return result;
}

const NOTIFY_CASE_POSTED_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/notify-case-posted`;

/**
 * @description Fire-and-forget confirmation email after a case is posted —
 *   case receipt + a walkthrough of the actual pipeline (Applications →
 *   review → interview → offer → hire) plus Show Therapists / Boost. Never
 *   awaited by the caller, never blocks the post-case success flow. Same
 *   pattern as applicationService.js's _emailApplicationStatus.
 * @param {string} caseId
 */
export function notifyCasePosted(caseId) {
  if (!caseId) return;
  try {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.access_token) return;
      fetch(NOTIFY_CASE_POSTED_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session.access_token}` },
        body: JSON.stringify({ case_id: caseId }),
      }).catch(err => console.warn("[notifyCasePosted] failed (non-fatal):", err?.message));
    }).catch(() => {});
  } catch { /* never block the post-case flow on email */ }
}

// CRIT-R6-2: All case-mutation functions now include an explicit agencyId predicate
// so they fail even if an RLS policy is misconfigured. The agencyId is the
// authenticated user's own ID, passed in by the caller.
export async function updateCase(caseId, agencyId, form, coords) {
  const rawForm = form;
  form = normalizeListingForm(form);
  const positionErrors = positionSubmitErrors(rawForm, form);
  if (positionErrors.length) throw new Error(positionErrors.join(" "));
  // PHI gate
  if (isPosition(form)) {
    const metadataContact = scanForPHI({ title: form.positionDetails.jobTitle, support: form.positionDetails.supportNotes, qualifications: form.positionDetails.qualifications });
    if (metadataContact.warnings.length) {
      const err = new Error('Remove phone numbers and email addresses from position details. Use platform messaging to share contact information.');
      err.code = 'CONTACT_INFO_DETECTED';
      throw err;
    }
  }
  const phi = scanForPHI({ notes: form.notes, schedule_notes: form.scheduleNotes, supervision: form.supervision, ...(isPosition(form) ? { position_title: form.positionDetails.jobTitle, support_notes: form.positionDetails.supportNotes, qualifications: form.positionDetails.qualifications } : {}) });
  if (phi.blocked.length > 0) {
    const err = new Error(
      `This listing appears to contain protected health information (${phi.blocked.join(", ")}). ` +
      `Please remove it before saving. ABAcases is not permitted to receive identifying client data.`
    );
    err.code = "PHI_DETECTED";
    err.phiPatterns = phi.blocked;
    throw err;
  }

  // Block edits when the starter trial has expired (slot check includes trial state)
  const slot = await checkPostingSlot(agencyId);
  if (slot.trial_expired) {
    const err = new Error("Your free trial has ended. Upgrade to Pro to edit case listings.");
    err.code = "TRIAL_EXPIRED";
    err.slotInfo = slot;
    throw err;
  }
  const safeCoords = await resolveCaseCoords(form, coords);
  const { agency_id, agency_name, status, confirmed_at, expires_at, ...fields } = formToDb(null, null, form, safeCoords);
  // Never wipe good coordinates on edit. If the geocode couldn't be resolved
  // this save (e.g. a transient API failure or a client that blocks the
  // lookup), drop the location columns from the update so the row keeps the
  // coordinates it already had — overwriting them with null would silently
  // remove the case from the map. A genuine ZIP change re-geocodes
  // successfully and DOES update them.
  if (!safeCoords) {
    delete fields.lat; delete fields.lng; delete fields.city; delete fields.state;
  }
  const { error } = await supabase.from("cases").update(fields).eq("id", caseId).eq("agency_id", agencyId);
  if (error) {
    logCaughtError(error, "case_update_failed", { listingId: caseId, step: "update" });
    throw error;
  }

  // Urgency toggle handling on edit:
  //   - User checked "Mark Urgent" + no active urgency_badge promo → spend a credit
  //   - User cleared "Mark Urgent" + has an active urgency_badge promo → cancel it
  //     (we DO NOT refund the credit — it's a one-time spend per the existing
  //     boost product semantics; cancelling just stops the badge going forward)
  // The trigger urgency_badge_promo_sync handles the cases.urgency column.
  const wantsUrgent = form.urgency === "high";
  const { data: existing } = await supabase
    .from("case_promotions")
    .select("id")
    .eq("case_id", caseId)
    .eq("type", "urgency_badge")
    .eq("status", "active")
    .maybeSingle();

  if (wantsUrgent && !existing) {
    try {
      const cfg = await loadAllConfigs().catch(() => null);
      const products = getBoostProducts(cfg);
      const result = await spendCredits(agencyId, {
        caseId,
        productKey: "urgency_badge",
        boostProducts: products,
      });
      if (!result?.ok) {
        const err = new Error(result?.error || "Could not apply urgency badge.");
        err.code = "URGENCY_SPEND_FAILED";
        err.urgencyBadgeError = result?.error;
        throw err;
      }
    } catch (e) {
      if (e?.code === "URGENCY_SPEND_FAILED") throw e;
      const err = new Error("Could not apply urgency badge.");
      err.code = "URGENCY_SPEND_FAILED";
      err.urgencyBadgeError = e?.message;
      throw err;
    }
  } else if (!wantsUrgent && existing) {
    // Cancel — trigger clears cases.urgency. No refund.
    await supabase
      .from("case_promotions")
      .update({ status: "cancelled" })
      .eq("id", existing.id);
  }
}

/**
 * @description True when a failure is the database refusing a write because the
 *   agency has no open case slot left (guard_case_client_writes raises
 *   check_violation with HINT 'SLOT_LIMIT'). Accepts either a raw supabase-js
 *   PostgrestError (hint on the error) or an Error this module already tagged
 *   with code 'SLOT_LIMIT'.
 * @param {any} err
 * @returns {boolean}
 */
export function isSlotLimitError(err) {
  return !!err && (err.code === "SLOT_LIMIT" || err.hint === "SLOT_LIMIT");
}

// Re-throw a raw database error, tagging a slot refusal with code 'SLOT_LIMIT'
// so every caller can branch on one value (the same code the client-side
// pre-checks in resumeCase / restoreCase / postCase already use).
//
// Anything else is an unexpected database refusal (constraint, RLS, network) and is
// logged to Admin > Logs as case_<op>_failed before it is re-thrown. A slot refusal
// is an expected, explained outcome and is not logged.
function throwCaseWriteError(error, op = "write", meta = {}) {
  if (isSlotLimitError(error)) {
    const err = new Error(
      "No open case slots on your plan. Close a case or add a slot in Billing."
    );
    err.code = "SLOT_LIMIT";
    throw err;
  }
  logCaughtError(error, `case_${op}_failed`, { op, ...meta });
  throw error;
}

export async function toggleCaseStatus(caseId, agencyId, newStatus) {
  // Going back to active ends any freshness pause, so the stamp goes with it.
  // A stale stamp left on a revived case would make a LATER manual pause look
  // like it had been sitting stale for months, and auto_archive_paused_cases()
  // measures from exactly that column.
  // It also takes the posting slot back: a case reopened from filled/closed was
  // released (slot_released = true) and must count against the plan cap again.
  // The database enforces the same rule and the cap itself (SLOT_LIMIT).
  const patch = newStatus === "active"
    ? { status: newStatus, slot_released: false, stale_paused_at: null, stale_pause_applicants: null, expiry_notice_stage: null }
    : { status: newStatus };
  const { error } = await supabase
    .from("cases")
    .update(patch)
    .eq("id", caseId)
    .eq("agency_id", agencyId);
  if (error) throwCaseWriteError(error, "status_change", { listingId: caseId, step: newStatus });
}

// ── Agency-initiated close — soft close that returns the posting slot ─────────
// This is the ONLY path that sets slot_released = true.
// Admin archive, stale expiry, and pause do NOT call this function.
export async function closeCase(caseId, agencyId) {
  const { error } = await supabase
    .from("cases")
    .update({ status: "closed", slot_released: true })
    .eq("id", caseId)
    .eq("agency_id", agencyId);
  if (error) {
    logCaughtError(error, "case_close_failed", { listingId: caseId, step: "close" });
    throw error;
  }
  trackEvent(agencyId, EVENTS.CASE_CLOSED, { case_id: caseId });
}

const NOTIFY_STATUS_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/notify-application-status`;

/**
 * @description Fire-and-forget "case closed" notification (email + in-app) to
 *   every OTHER still-open applicant after an agency closes a case with a
 *   reason. Same posture as applicationService.js's _emailApplicationStatus:
 *   never awaited by the caller, never throws, never surfaces as a close
 *   error — the close itself has already succeeded by the time this fires.
 *
 *   Reuses notify-application-status's existing `case_filled` fan-out (do not
 *   add a second notify path here) — it does the actual work of picking the
 *   right reason-aware copy, writing the in-app notification, and emailing
 *   each recipient. This is one of TWO callers of that fan-out; the other is
 *   case-respond's service-role "Mark filled" email-link action, which has no
 *   client session and calls the edge function directly.
 * @param {string} caseId
 * @param {string} reason - one of close_case_with_reason's six reason codes, or an admin_closed_* code (admin
 *   closes; the edge function accepts an admin caller for this kind only and sends the neutral "no longer
 *   available" copy, which never states the reason)
 */
export function _notifyCaseClosed(caseId, reason) {
  if (!caseId) return;
  try {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.access_token) return;
      fetch(NOTIFY_STATUS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session.access_token}` },
        body: JSON.stringify({ case_id: caseId, kind: "case_filled", reason }),
      }).catch(err => console.warn("[closeCaseWithReason] notify failed (non-fatal):", err?.message));
    }).catch(() => {});
  } catch { /* never block the close flow on notifying applicants */ }
}

/**
 * @description Close a case with a typed reason code + optional note.
 *   Wraps the close_case_with_reason RPC. The note is appended to the
 *   case journal as a system event ("Case closed — {reason}\n\n{note}"),
 *   always — regardless of plan, since closure documentation is workflow
 *   evidence rather than a paid feature.
 *
 * @param {string} caseId
 * @param {"filled"|"filled_elsewhere"|"couldnt_fill"|"returned_to_client"|"duplicate"|"other"} reason
 * @param {string} [note] - Optional free-text context recorded in the journal.
 * @returns {Promise<{ok: boolean, case_id?: string, reason?: string, note_recorded?: boolean, error?: string}>}
 */
export async function closeCaseWithReason(caseId, reason, note = null) {
  if (!caseId) return { ok: false, error: "case_id required" };
  const { data, error } = await supabase.rpc("close_case_with_reason", {
    p_case_id: caseId,
    p_reason:  reason,
    p_note:    note && String(note).trim().length > 0 ? String(note).trim() : null,
  });
  if (error) {
    // eslint-disable-next-line no-console
    console.warn("[caseService] closeCaseWithReason failed:", error);
    return { ok: false, error: error.message || "Could not close case" };
  }
  // Audit event — actor is the current session user (the RPC authorizes via
  // auth.uid() server-side). getSession() is a local read, no network call.
  if (data?.ok) {
    supabase.auth.getSession().then(({ data: s }) => {
      const uid = s?.session?.user?.id;
      if (uid) trackEvent(uid, EVENTS.CASE_CLOSED, { case_id: caseId, reason });
    }).catch(() => {});
    // Tell the other still-open applicants the listing is gone — the gap
    // this closes: the RPC cascades their application status to `closed` in
    // SQL, but sends nothing itself. Fire-and-forget; a notify failure must
    // never turn a successful close into a reported error.
    _notifyCaseClosed(caseId, reason);
  }
  return data || { ok: false, error: "no_response" };
}

/**
 * @description Display metadata for the four coverage_gap_status values.
 *   Centralizes the labels, colors, banner text, and primary-action label
 *   so the Map pin treatment, card banner, filter chip, and KPI strip
 *   stay in sync without each surface re-deriving them.
 *
 *   ABA-grounded framing: actions point at things the agency can ACTUALLY
 *   do — direct outreach, renegotiate listing, mark closed. Pay raises and
 *   radius widening are not in the ABA agency's lever set (Medicaid base
 *   rate is fixed; the case is at the client's home/school).
 */
export const COVERAGE_GAP_META = {
  ok: {
    label:           null,        // no badge
    badgeBg:         null,
    badgeColor:      null,
    bannerText:      null,
    primaryAction:   null,
    isFlagged:       false,
  },
  gap_no_supply: {
    label:           "No supply nearby",
    badgeBg:         "#FEF2F2",
    badgeColor:      "#9A1D1D",
    bannerText:      "0 providers within 15 miles match this case type. Geographic dead zone. Consider renegotiating with the parent or marking closed.",
    primaryAction:   "Edit listing",      // open the listing for renegotiation
    secondaryAction: "Mark closed",
    isFlagged:       true,
  },
  gap_few_matches: {
    label:           "Few matches",
    badgeBg:         "#FEF7E6",
    badgeColor:      "#92400E",
    bannerText:      "Providers exist nearby but few are strong matches for this case. The map can show who's reachable.",
    primaryAction:   "Show providers nearby",
    secondaryAction: "Edit listing",
    isFlagged:       true,
  },
  gap_no_conversion: {
    label:           "Not converting",
    badgeBg:         "#FEF7E6",
    badgeColor:      "#92400E",
    bannerText:      "Strong matches are seeing this case but not applying. Reach out directly or audit the listing.",
    primaryAction:   "Show providers nearby",
    secondaryAction: "Edit listing",
    isFlagged:       true,
  },
};

// Close reasons (CLOSED_REASON_META, closedReasonLabel) live in their own pure module: lib/closeReasons.js.

/**
 * @description Trigger a coverage-gap recompute for a single case. Useful
 *   for testing or when the agency wants a "refresh" beyond the daily cron
 *   and on-write trigger. The RPC writes directly back to cases.
 * @param {string} caseId
 * @returns {Promise<Object|null>} The diagnostic JSON or null on error.
 */
export async function recomputeCaseCoverageGap(caseId) {
  if (!caseId) return null;
  const { data, error } = await supabase.rpc("compute_case_coverage_gap", {
    p_case_id: caseId,
  });
  if (error) {
    // eslint-disable-next-line no-console
    console.warn("[caseService] recomputeCaseCoverageGap failed:", error);
    return null;
  }
  return data || null;
}

// ── Confirm a case is still active (resets freshness clock) ───────────────────
export async function confirmCase(caseId, agencyId, caseType = "RBT") {
  const now = new Date();
  const { error } = await supabase
    .from("cases")
    .update({
      confirmed_at: now.toISOString(),
      expires_at: buildExpiryIso(caseType, now),
      // Clear the freshness-pause stamp. It marks an OPEN pause; leaving it set
      // on a revived case would let the stale alert fire for a case that is
      // live again, which is the bug this column exists to prevent.
      stale_paused_at: null,
      stale_pause_applicants: null,
      // Reconfirming starts a new expiry-notice cycle — see the column's
      // COMMENT (migration 20260918160000). Without this, a case that had
      // already gotten its stage-2 "last call" email would reconfirm, run out
      // the clock again, and never get a stage-1 nudge on the new cycle.
      expiry_notice_stage: null,
    })
    .eq("id", caseId)
    .eq("agency_id", agencyId);
  if (error) throw error;

  trackEvent(agencyId, EVENTS.CASE_CONFIRM, { case_id: caseId });

  // Log the renew to the case journal so the freshness event surfaces in the
  // Notebook timeline alongside other lifecycle events (created / closed /
  // applications / etc.). Best-effort — a journal write failure must not roll
  // back the user-visible reconfirm. Done at the service layer so every code
  // path that renews (modal Renew button, future bulk renew, dashboard CTAs)
  // gets the same journal entry without reimplementing it at each call site.
  supabase
    .from("case_journal")
    .insert({
      case_id:   caseId,
      agency_id: agencyId,
      type:      "system",
      event:     "listing_confirmed",
      body:      "Freshness reconfirmed",
    })
    .then(() => {})
    .catch(() => {}); // swallow — journal is supplementary, not load-bearing
}

// ── Bulk reconfirm: reactivate ALL of an agency's freshness-paused cases ──────
// One-click recovery — turns a board of auto-paused listings back on without
// per-case clicking. Excludes moderation-flagged (admin-paused) cases, which
// must stay paused. Resets the freshness clock so reactivated cases re-rank
// well, and logs each to the journal. Returns the number reactivated.
export async function reactivateAllPausedCases(agencyId) {
  if (!agencyId) return { count: 0 };
  const now = new Date();

  // Respect the plan's active cap — never resurrect more than the agency can
  // legitimately have visible, or an over-limit board could be brought fully
  // active for free. -1 (enterprise) = unlimited.
  const slot = await checkPostingSlot(agencyId).catch(() => null);
  let limitN = null; // null = unlimited
  if (slot && slot.total_limit !== -1) {
    const { count: activeCount } = await supabase
      .from("cases")
      .select("id", { count: "exact", head: true })
      .eq("agency_id", agencyId)
      .eq("status", "active");
    limitN = Math.max(0, slot.total_limit - (activeCount || 0));
    if (limitN === 0) return { count: 0, capped: true };
  }

  // Pick the paused (non-flagged) cases to bring back, newest first, up to the
  // number of free active slots.
  let q = supabase
    .from("cases")
    .select("id")
    .eq("agency_id", agencyId)
    .eq("status", "paused")
    .or("flagged.is.null,flagged.eq.false")
    .order("created_at", { ascending: false });
  if (limitN !== null) q = q.limit(limitN);
  const { data: picked, error: pErr } = await q;
  if (pErr) throw pErr;
  const ids = (picked || []).map(r => r.id);
  if (ids.length === 0) return { count: 0 };

  const { error } = await supabase
    .from("cases")
    .update({
      status:          "active",
      confirmed_at:    now.toISOString(),
      expires_at:      buildExpiryIso("RBT", now),
      freshness_state: "fresh",
      stale_paused_at: null,          // the pause is over — see confirmCase()
      stale_pause_applicants: null,
      expiry_notice_stage: null,      // new cycle — see confirmCase()
    })
    .in("id", ids);
  if (error) throwCaseWriteError(error, "bulk_resume", { count: ids.length });

  supabase.from("case_journal").insert(
    ids.map(id => ({ case_id: id, agency_id: agencyId, type: "system", event: "listing_confirmed", body: "Reconfirmed (bulk)" }))
  ).then(() => {}).catch(() => {});
  ids.forEach(id => trackEvent(agencyId, EVENTS.CASE_CONFIRM, { case_id: id, bulk: true }));
  return { count: ids.length, capped: limitN !== null && ids.length >= limitN };
}

// ── Resume a single paused case back to active (respects the active cap) ──────
// A paused listing still holds its slot, but flipping it back to ACTIVE
// (visible to therapists) must respect the plan limit — otherwise an over-limit
// agency could bypass billing via pause→resume. The cap is on visible/active
// listings: resume is allowed only while active count < total_limit. -1 =
// unlimited. Admin-flagged cases stay paused.
export async function resumeCase(caseId, agencyId) {
  const slot = await checkPostingSlot(agencyId);
  if (slot && slot.total_limit !== -1) {
    const { count: activeCount } = await supabase
      .from("cases")
      .select("id", { count: "exact", head: true })
      .eq("agency_id", agencyId)
      .eq("status", "active");
    if ((activeCount || 0) >= slot.total_limit) {
      const err = new Error(
        `Your ${slot.plan} plan allows ${slot.total_limit} active listing${slot.total_limit === 1 ? "" : "s"}. ` +
        `End an active listing, or upgrade / add case slots to bring paused cases back.`
      );
      err.code = "SLOT_LIMIT";
      err.slotInfo = slot;
      throw err;
    }
  }
  const now = new Date();
  const { error } = await supabase
    .from("cases")
    .update({
      status:          "active",
      confirmed_at:    now.toISOString(),
      expires_at:      buildExpiryIso("RBT", now),
      freshness_state: "fresh",
      stale_paused_at: null,          // the pause is over — see confirmCase()
      stale_pause_applicants: null,
      expiry_notice_stage: null,      // new cycle — see confirmCase()
    })
    .eq("id", caseId)
    .eq("agency_id", agencyId)
    .eq("status", "paused")
    .or("flagged.is.null,flagged.eq.false");
  if (error) throwCaseWriteError(error, "resume", { listingId: caseId });
}

// ── Agency's own cases (excludes archived — they have their own loader) ──────
export async function loadAgencyCases(agencyId) {
  const { data, error } = await supabase
    .from("cases")
    .select(`
      id, case_code, agency_id, agency_name, dba_name, type, listing_kind, position_details,
      employment, status, hours, pay_range, pay_min,
      schedule_notes, start_date, service_time, age_range, age_min, age_max, diagnosis_types,
      payer_types, must_haves, must_have_skills, languages, client_initials, client_sex, therapist_sex_pref,
      zip, city, state, lat, lng, setting, delivery_mode, skills, session_types, min_exp,
      supervision, training_provided, bcba_support, mileage_reimbursement,
      benefits, team_size, notes, urgency, caseload_size, billable_hrs,
      rbt_count, assessments, requirements, supervision_model,
      confirmed_at, expires_at, archived_at, views, created_at, slot_released,
      coverage_gap_status, matched_therapists_count, therapists_in_radius_count,
      coverage_gap_computed_at, closed_reason, flagged, flagged_reason, flagged_at,
      rank_score, rank_tier, rank_trend, active_boost_types
    `)
    .eq("agency_id", agencyId)
    .neq("status", "archived")
    .order("created_at", { ascending: false })
    .limit(500); // agencies are unlikely to have >500 active cases

  if (error) throw error;
  const cases = (data || []).map(dbToCase);

  // Attach the latest Reach Blast timestamp per case. A reach blast is recorded
  // in outreach_log (NOT on active_boost_types), so without this the engine
  // can't tell a case was just blasted and would keep recommending "Reach
  // Blast" forever. The recommendation engine reads lastReachBlastAt to apply a
  // post-blast cooldown. Best-effort: a failure here just means no cooldown.
  try {
    const ids = cases.map(c => c.id);
    if (ids.length) {
      const { data: logs } = await supabase
        .from("outreach_log")
        .select("case_id, sent_at")
        .eq("agency_id", agencyId)
        .eq("type", "blast")
        .in("case_id", ids)
        .order("sent_at", { ascending: false });
      if (logs?.length) {
        const latest = {};
        for (const r of logs) { if (!latest[r.case_id]) latest[r.case_id] = r.sent_at; }
        for (const c of cases) { c.lastReachBlastAt = latest[c.id] || null; }
      }
    }
  } catch { /* no cooldown signal on failure — never break the case list */ }

  return cases;
}

// ── One of the agency's own openings, whole (any status) ────────────────────
// For "Repost" / "Post a similar opening" from Insights between openings: the posting form is filled in
// from it as a NEW posting. RLS limits the read to the caller's agency (owner or teammate).
export async function loadOwnCase(caseId) {
  if (!caseId) return null;
  const { data, error } = await supabase.from("cases").select("*").eq("id", caseId).maybeSingle();
  if (error) {
    logCaughtError(error, "case_load_own_failed", { listingId: caseId });
    return null;
  }
  return data ? dbToCase(data) : null;
}

// ── Archive a closed/filled case — moves it out of the main dashboard ────────
export async function archiveCase(caseId, agencyId) {
  const { error } = await supabase
    .from("cases")
    .update({ status: "archived", slot_released: true, archived_at: new Date().toISOString() })
    .eq("id", caseId)
    .eq("agency_id", agencyId)
    .in("status", ["closed", "filled"]); // can only archive closed/filled cases
  if (error) throw error;
}

// ── End (archive) a PAUSED listing — frees its slot ──────────────────────────
// The billing-recovery "End listing" action. archiveCase only accepts
// closed/filled, so it silently no-ops on a paused case; this archives a paused
// listing directly (status→archived, slot released) so it stops counting toward
// the plan cap. Reversible via restoreCase (Archived tab).
export async function endPausedListing(caseId, agencyId) {
  const { error } = await supabase
    .from("cases")
    .update({ status: "archived", slot_released: true, archived_at: new Date().toISOString() })
    .eq("id", caseId)
    .eq("agency_id", agencyId)
    .eq("status", "paused");
  if (error) throw error;
}

// ── Restore an archived case back to active (consumes a posting slot) ────────
export async function restoreCase(caseId, agencyId) {
  // Check trial expiry and slot availability before restoring
  const slot = await checkPostingSlot(agencyId);
  if (slot.trial_expired) {
    const err = new Error("Your free trial has ended. Upgrade to Pro to reopen case listings.");
    err.code = "TRIAL_EXPIRED";
    err.slotInfo = slot;
    throw err;
  }
  if (!slot.can_post) {
    const err = new Error(
      `No posting slots available on your ${slot.plan} plan. Close an existing listing first, or upgrade.`
    );
    err.code = "SLOT_LIMIT";
    err.slotInfo = slot;
    throw err;
  }
  const now = new Date();
  const { error } = await supabase
    .from("cases")
    .update({
      status: "active",
      slot_released: false,
      archived_at: null,
      confirmed_at: now.toISOString(),
      expires_at: buildExpiryIso("RBT", now), // reset freshness
      stale_paused_at: null,                  // the pause is over — see confirmCase()
      stale_pause_applicants: null,
      expiry_notice_stage: null,              // new cycle — see confirmCase()
    })
    .eq("id", caseId)
    .eq("agency_id", agencyId)
    .eq("status", "archived");
  if (error) throwCaseWriteError(error, "restore", { listingId: caseId });
}

// ── Load archived cases for an agency ───────────────────────────────────────
export async function loadArchivedCases(agencyId) {
  const { data, error } = await supabase
    .from("cases")
    .select("*")
    .eq("agency_id", agencyId)
    .eq("status", "archived")
    .order("archived_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(dbToCase);
}

// ── Increment view counter — called when a therapist opens a case detail ──────
// MIN-R5-3: The DB RPC deduplicates on (case_id, view_date) — one row per case
// per calendar day regardless of how many unique viewers. It does NOT dedup per
// user within a session. This module-level Set provides session-level dedup so
// repeatedly opening the same case modal in one session counts only once.
// The Set is intentionally not persisted (in-memory only).
const _viewedThisSession = new Set();

export async function incrementCaseViews(caseId) {
  if (!caseId) return false;
  if (_viewedThisSession.has(caseId)) return false; // already counted this session
  _viewedThisSession.add(caseId);
  // Param is p_case_id — the decoupled counter RPC writes to case_views_daily.
  // (Calling with the old `case_id` name returned 404: PostgREST matches RPCs
  // by argument name, and increment_case_views(case_id) doesn't exist.)
  await supabase.rpc("increment_case_views", { p_case_id: caseId });
  return true;
}

// ── Case view sources (agency-facing) ────────────────────────────────────────
// Where the views on a case came from. Returns { [caseId]: [{ source, views }] }
// sorted by views desc; `source: "not_recorded"` carries views from before source
// logging began, so a case's rows add up to its Views number. Backed by the
// agency_case_view_sources RPC (owner/admin only; events is admin-read under RLS).
// `days` limits to the last N calendar days; omit for lifetime.
export async function loadCaseViewSources(caseIds, { days = null } = {}) {
  const ids = [...new Set((caseIds || []).filter(Boolean))];
  if (!ids.length) return {};
  const { data, error } = await supabase.rpc("agency_case_view_sources", {
    p_case_ids: ids,
    p_days: days,
  });
  if (error) throw error;
  const out = {};
  for (const r of data || []) {
    (out[r.case_id] ||= []).push({ source: r.source, views: Number(r.views) || 0 });
  }
  for (const id of Object.keys(out)) out[id].sort((a, b) => b.views - a.views);
  return out;
}

// ── Case save counts (agency-facing) ─────────────────────────────────────────
// Returns { [caseId]: count } for cases the agency owns.
// Requires the saved_cases_agency_read RLS policy (migration 20260406000002).
export async function loadCaseSaveCounts(caseIds) {
  if (!caseIds || caseIds.length === 0) return {};
  const { data, error } = await supabase
    .from("saved_cases")
    .select("case_id")
    .in("case_id", caseIds)
    .eq("action", "saved");
  if (error) return {};
  return (data || []).reduce((acc, row) => {
    acc[row.case_id] = (acc[row.case_id] || 0) + 1;
    return acc;
  }, {});
}

// ── Platform-wide benchmarks by case type ────────────────────────────────────
//
// Aggregates platform-wide performance metrics for each case type (RBT, BCBA,
// BCABA) over the last 90 days so the agency-side Insights page can show a
// "vs. similar agencies" comparison column. Two distinct numbers per type:
//   - avgViews:   average lifetime views per case
//   - avgConv:    avg conversion % (applicants / views, capped at 100)
//   - avgDaysToFill: avg days from posted to hired, other agencies' hires in the last 180 days
//   - fillSampleSize: how many peer fills that average rests on
//   Peers are other agencies only (never the caller), BT counted as RBT (case_type_benchmarks).
//   - sampleSize: how many cases informed the average — surfaced as "n=X"
//                 in the UI so agencies can judge whether the comparison is
//                 statistically meaningful.
//
// Cheap to compute — single SELECT against cases + applications.
// Cached on the client (queryCache) for an hour because the values are stable
// at a platform scale.
export async function loadCaseTypeBenchmarks() {
  // Aggregated server-side by case_type_benchmarks (agency / admin only). The
  // old client path pulled every visible case, including other agencies' cases
  // with their per-case view counts, and averaged them here; now only the
  // per-type aggregates cross the wire.
  const { data, error } = await supabase.rpc("case_type_benchmarks");
  if (error) throw error;
  const num = (v) => (v == null ? null : Number(v));
  const out = { rbt: null, bcba: null, bcaba: null };
  for (const r of (data || [])) {
    const key = String(r.case_type || "").toLowerCase();
    if (!(key in out)) continue;
    out[key] = {
      sampleSize:     num(r.sample_size) ?? 0,
      // Cases that actually carry traffic (views > 0). The conversion baseline
      // is only trustworthy when enough peers have real views — otherwise
      // avgConv is dragged toward 0 by hundreds of zero-view seed cases and a
      // "+14% vs similar" really means "+14 above an empty baseline." The UI
      // gates the vs-cohort column on this, NOT on the raw sampleSize.
      convSampleSize: num(r.conv_sample_size) ?? 0,
      avgViews:       num(r.avg_views),
      avgApps:        num(r.avg_apps),
      avgConv:        num(r.avg_conv),
      avgDaysToFill:  num(r.avg_days_to_fill),
      // Peer fills behind avgDaysToFill (the server returns the average only from 5 fills across 3+ agencies).
      fillSampleSize: num(r.fill_sample_size) ?? 0,
    };
  }
  return out;
}

// ── New-case freshness (polling, not realtime) ───────────────────────────────
/**
 * Counts active cases created since a timestamp, scoped to a geographic
 * bounding box and (optionally) a set of role types. Drives the therapist
 * map's "N new cases in your area" banner.
 *
 * Why polling instead of realtime: a realtime subscription on `cases` is a
 * BROADCAST channel — every new case (and previously, every view-counter
 * write) fans out to ALL connected map viewers, costing one RLS evaluation
 * per subscriber per event. At 1k concurrent users that's quadratic and
 * does not scale. A periodic indexed COUNT is flat per-client cost and uses
 * the idx_cases_status_active partial index. The map already refetches the
 * full result set on pan/filter; this only powers the lightweight banner.
 *
 * @param {string} sinceIso  - ISO timestamp; count cases created after this
 * @param {{minLat:number,maxLat:number,minLng:number,maxLng:number}} bounds
 * @param {string[]} [types] - Optional role filter (e.g. ["RBT","BCBA"])
 * @returns {Promise<number>} Count of new in-bounds active cases
 */
export async function countNewActiveCasesSince(sinceIso, bounds, types) {
  if (!sinceIso || !bounds) return 0;
  const { data, error } = await supabase.rpc("count_new_active_cases", {
    p_since:   sinceIso,
    p_min_lat: bounds.minLat,
    p_max_lat: bounds.maxLat,
    p_min_lng: bounds.minLng,
    p_max_lng: bounds.maxLng,
    p_types:   types && types.length > 0 ? types : null,
  });
  if (error) return 0;
  return Number(data) || 0;
}

// Columns the homepage case card may read. All are plain facts about the role
// (what, where, pay, hours, schedule days, perks) and the already-widened client
// age range. Deliberately NOT here: client_initials, agency_id/agency_name/
// dba_name, zip, lat/lng, notes, schedule_notes or any other free text.
// This list is the contract of the public_open_cases() function (a Jest test
// pins the two together); the client no longer selects it from the table.
export const PUBLIC_OPEN_CASE_COLUMNS = [
  "id", "type", "listing_kind", "city", "state", "pay_range", "setting", "delivery_mode", "created_at",
  "hours", "employment", "age_range", "age_min", "age_max", "service_time",
  "training_provided", "bcba_support", "mileage_reimbursement",
].join(", ");

/**
 * @description Loads a small sample of live active cases for the public
 *   (guest) home page's "open cases" carousel through public_open_cases (a
 *   SECURITY DEFINER function), not the cases table, so the projection is
 *   enforced by the database: no lat/lng, ZIP, agency identity, client initials
 *   or free text. Never throws — this is a public marketing page and must
 *   degrade silently on any error.
 * @param {number} [limit=12] - Max cases to return
 * @returns {Promise<Object[]>} Array of raw case rows (PUBLIC_OPEN_CASE_COLUMNS),
 *   or [] on error
 */
export async function loadPublicOpenCases(limit = 12) {
  try {
    const { data, error } = await supabase.rpc("public_open_cases", { p_limit: limit });
    if (error) return [];
    return data || [];
  } catch {
    return [];
  }
}

/**
 * @description Do any live public cases of the page's role exist for a city-page area? Powers the "Cases are open
 *   near X now" line and FAQ answer on the role x city SEO pages, which must only say so when we actually know it.
 *   Count-only function (public_open_case_count); no rows are returned, so nothing about a case is exposed.
 * @param {{state: string, cities?: string[], types?: string[]}} area - 2-letter state, the city names to match
 *   (omit/empty for a statewide hub = whole state), and the case types to count (findCasesQuery.seoCaseTypesForRole;
 *   omit = every type)
 * @returns {Promise<boolean|null>} true = at least one live case, false =
 *   none, null = unknown (query failed or blocked, e.g. during prerender)
 */
export async function hasPublicOpenCasesNear({ state, cities, types } = {}) {
  if (!state) return null;
  try {
    const { data: count, error } = await supabase.rpc("public_open_case_count", {
      p_state:  state,
      p_cities: Array.isArray(cities) && cities.length > 0 ? cities : null,
      // Only the case types the page is about (seoCaseTypesForRole); omitted = every type.
      ...(Array.isArray(types) && types.length > 0 ? { p_types: types } : {}),
    });
    if (error || typeof count !== "number") return null;
    return count > 0;
  } catch {
    return null;
  }
}
